import type {
  EChartsOption,
  FunnelSeriesOption,
  PieSeriesOption,
  ScatterSeriesOption,
  TreemapSeriesOption,
} from 'echarts';
import { format } from 'echarts/core';
import { hierarchy, pack } from 'd3-hierarchy';

import formatFloat from 'api/getChartSeries/formatFloat';

import {
  layoutPieLabels,
  PIE_LABEL_LINE_LENGTH,
  PIE_LABEL_LINE_LENGTH2,
} from './pieLabels';
import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// A part as it's labelled and listed: a pie's share, anything else its
// metric's value
const formatPartValue = ({
  model,
  value,
  formatMetric,
}: {
  model: StudioCharts.PartsModel;
  value: number | null;
  formatMetric: FormatMetric;
}) =>
  model.kind === 'pie'
    ? `${formatFloat(value, model.sharePrecision)}%`
    : formatMetric({ metricId: model.metricId, value });

// Each part in its colour, or greyed where a drill-down picked others
const toData = ({
  model,
  theme,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
}) =>
  model.parts
    .filter((part) => part.value !== null)
    .map((part) => ({
      name: part.name,
      value: part.value as number,
      itemStyle: {
        color:
          model.selectedKeys && !model.selectedKeys.includes(part.key)
            ? theme.deselected
            : part.color,
      },
    }));

const toPieSeries = ({
  model,
  theme,
  size,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  size: { width: number; height: number };
}): PieSeriesOption => {
  const data = toData({ model, theme });
  // Sized and placed for its labels, the smallest parts' left out where they
  // can't sit near their slices, as the Highcharts pie's are
  const { radius, centerX, labelledIndexes } = layoutPieLabels({
    parts: data,
    size,
    fontFamily: theme.fontFamily,
  });
  return {
    type: 'pie',
    id: model.seriesId,
    name: model.name,
    center: [centerX, '50%'],
    // Solid, or with a donut's hole
    radius: [(model.hole ?? 0) * radius, radius],
    data: data.map((item, index) =>
      labelledIndexes.has(index)
        ? item
        : { ...item, label: { show: false }, labelLine: { show: false } },
    ),
    // Its slices parted by the background, as the Highcharts pie's
    itemStyle: { borderColor: theme.background, borderWidth: 1 },
    label: {
      show: true,
      formatter: '{b}',
      color: theme.textPrimary,
      fontSize: FONT_SIZE,
      // Wrapped at spaces where there isn't room beside the pie, as
      // Highcharts' are, rather than cut off
      overflow: 'break',
    },
    labelLine: {
      length: PIE_LABEL_LINE_LENGTH,
      length2: PIE_LABEL_LINE_LENGTH2,
      lineStyle: { color: theme.axisLine },
    },
    // Any labels that still overlap are hidden
    avoidLabelOverlap: true,
    labelLayout: { hideOverlap: true },
    emphasis: { scale: true, scaleSize: 4 },
  };
};

const toFunnelSeries = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): FunnelSeriesOption => ({
  type: 'funnel',
  id: model.seriesId,
  name: model.name,
  // Largest first: at the top of a funnel, at the base of a pyramid
  sort: model.isPyramid ? 'ascending' : 'descending',
  // A funnel narrows to a stem; a pyramid to a point
  minSize: model.isPyramid ? '0%' : '20%',
  // Room on the right for the labels
  left: '5%',
  width: '60%',
  top: 12,
  bottom: 12,
  gap: 2,
  data: toData({ model, theme }),
  label: {
    show: true,
    position: 'right',
    color: theme.textPrimary,
    fontSize: FONT_SIZE,
    // As the Highcharts funnel: the name in bold, its value after it
    formatter: ({ name, value }) =>
      `{name|${name}} (${formatPartValue({
        model,
        value: value as number,
        formatMetric,
      })})`,
    rich: { name: { fontWeight: 'bold', fontSize: FONT_SIZE } },
  },
  labelLine: { lineStyle: { color: theme.axisLine } },
});

// Room either side of a tile's label
const TREEMAP_LABEL_PADDING = 5;
// The smallest a tile's label shrinks to
const MIN_TREEMAP_FONT_SIZE = 8;

// A tile's label size: smaller where the tile is narrower than the label's
// longest word, so the word isn't broken or cut off (labels wrap at spaces)
const toTreemapLabelLayout =
  (fontFamily: string) =>
  ({ text, rect }: { text: string; rect: { width: number } }) => {
    const room = rect.width - 2 * TREEMAP_LABEL_PADDING;
    const font = `${FONT_SIZE}px ${fontFamily}`;
    const widest = Math.max(
      0,
      ...text.split(/\s+/).map((word) => format.getTextRect(word, font).width),
    );
    return widest <= room || room <= 0
      ? {}
      : {
          fontSize: Math.max(
            MIN_TREEMAP_FONT_SIZE,
            Math.floor((FONT_SIZE * room) / widest),
          ),
        };
  };

const toTreemapSeries = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): TreemapSeriesOption => ({
  type: 'treemap',
  id: model.seriesId,
  name: model.name,
  // Drawn as it is: no zooming into a tile, panning, or trail of parents
  nodeClick: false,
  roam: false,
  breadcrumb: { show: false },
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  data: toData({ model, theme }),
  itemStyle: { borderRadius: 2, gapWidth: 2, borderColor: theme.background },
  label: {
    show: true,
    color: theme.textOnColor,
    fontSize: FONT_SIZE,
    // Wrapped at spaces, as Highcharts' tree maps do, rather than cut off
    overflow: 'break',
    // Its value under its name where the chart shows values
    formatter: ({ name, value }) =>
      model.showValues
        ? `${name}:\n${formatPartValue({
            model,
            value: value as number,
            formatMetric,
          })}`
        : name,
  },
  labelLayout: toTreemapLabelLayout(theme.fontFamily),
});

// Room between packed bubbles, and around them
const BUBBLE_PADDING = 3;
// Used until the chart knows its size, e.g. in tests
const FALLBACK_SIZE = { width: 400, height: 300 };

export interface PackedBubble {
  name: string;
  value: number;
  color: string;
  // Its centre and radius, in pixels from the chart's top left
  x: number;
  y: number;
  r: number;
}

// Each part a circle, its area following its value, packed together in the
// room the chart has (d3's circle packing), as the Highcharts packed bubble
// chart gathers them, but laid out the same every time. Parts without a
// positive value have no circle.
export const toPackedBubbles = ({
  model,
  theme,
  size,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  size: { width: number; height: number };
}): PackedBubble[] => {
  const parts = toData({ model, theme }).filter((part) => part.value > 0);
  const root = pack<{ name: string; value: number; color: string }>()
    .size([size.width, size.height])
    .padding(BUBBLE_PADDING)(
    hierarchy<{
      name: string;
      value: number;
      color: string;
      children?: { name: string; value: number; color: string }[];
    }>({
      name: '',
      value: 0,
      color: '',
      children: parts.map((part) => ({
        name: part.name,
        value: part.value,
        color: part.itemStyle.color,
      })),
    }).sum((node) => (node.children ? 0 : node.value)),
  );
  return root.leaves().map((leaf) => ({
    name: leaf.data.name,
    value: leaf.data.value,
    color: leaf.data.color,
    x: leaf.x,
    y: leaf.y,
    r: leaf.r,
  }));
};

// Packed bubbles drawn where they were packed: on hidden axes as wide and
// tall as the chart, so a bubble's place and size are in pixels
const toPackedBubbleSeries = ({
  model,
  theme,
  size,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  size: { width: number; height: number };
}): ScatterSeriesOption => ({
  type: 'scatter',
  id: model.seriesId,
  name: model.name,
  data: toPackedBubbles({ model, theme, size }).map((bubble) => ({
    name: bubble.name,
    // Its value as the metric's, for the tooltip and label
    value: [bubble.x, bubble.y, bubble.value],
    symbolSize: bubble.r * 2,
    itemStyle: { color: bubble.color },
  })),
  label: {
    show: true,
    formatter: '{b}',
    color: theme.textOnColor,
    fontSize: FONT_SIZE,
  },
  emphasis: { scale: 1.05 },
});

// The ECharts options a pie (or donut), funnel (or pyramid), tree map or
// packed bubble chart draws with. None has a legend: each labels its parts.
const toPartsOption = ({
  model,
  theme,
  formatMetric,
  size = FALLBACK_SIZE,
}: {
  model: StudioCharts.PartsModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
  // The room pies are sized to and packed bubbles are packed into
  size?: { width: number; height: number };
}): EChartsOption => ({
  ...toBaseOption(theme),
  ...(model.kind === 'packedBubble'
    ? {
        grid: { left: 0, right: 0, top: 0, bottom: 0 },
        xAxis: { type: 'value', min: 0, max: size.width, show: false },
        // Down from the top, as the packing measures it
        yAxis: {
          type: 'value',
          min: 0,
          max: size.height,
          inverse: true,
          show: false,
        },
      }
    : {}),
  tooltip: {
    ...toTooltipStyle(theme),
    trigger: 'item',
    formatter: (params) => {
      const point = Array.isArray(params) ? params[0] : params;
      if (!point) {
        return '';
      }
      return toTooltipHtml({
        title: point.name,
        rows: [
          {
            marker: typeof point.marker === 'string' ? point.marker : '',
            name: model.name,
            value: formatPartValue({
              model,
              // A packed bubble's value follows its place
              value: (Array.isArray(point.value)
                ? point.value[2]
                : point.value) as number,
              formatMetric,
            }),
          },
        ],
      });
    },
  },
  series: [
    model.kind === 'pie'
      ? toPieSeries({ model, theme, size })
      : model.kind === 'funnel'
        ? toFunnelSeries({ model, theme, formatMetric })
        : model.kind === 'packedBubble'
          ? toPackedBubbleSeries({ model, theme, size })
          : toTreemapSeries({ model, theme, formatMetric }),
  ],
});

export default toPartsOption;
